/**
 * CSV Export Utilities
 * Convert transaction data to CSV format and download
 */

import Papa from "papaparse";
import { LedgerTransaction, WalletItem } from "../types";

/**
 * Export transactions to CSV file
 */
export const exportTransactionsToCSV = (
  transactions: LedgerTransaction[],
  fileName: string = "transactions.csv"
) => {
  if (!transactions || transactions.length === 0) {
    alert("No transactions to export");
    return;
  }

  // Format data for CSV
  const csvData = transactions.map((tx) => ({
    "Transaction ID": tx.txId,
    Type: tx.type,
    Amount: tx.amount ? `${tx.amount} NOK` : "N/A",
    Reason: tx.reason || "N/A",
    Status: tx.status,
    Date: tx.createdAt instanceof Date
      ? tx.createdAt.toLocaleDateString("no-NO")
      : new Date(tx.createdAt).toLocaleDateString("no-NO"),
    Time: tx.createdAt instanceof Date
      ? tx.createdAt.toLocaleTimeString("no-NO")
      : new Date(tx.createdAt).toLocaleTimeString("no-NO"),
  }));

  // Convert to CSV using papaparse
  const csv = Papa.unparse(csvData, {
    header: true,
    skipEmptyLines: true,
  });

  // Create blob and download
  downloadCSV(csv, fileName);
};

/**
 * Export wallet items to CSV file
 */
export const exportWalletItemsToCSV = (
  items: WalletItem[],
  fileName: string = "wallet-items.csv"
) => {
  if (!items || items.length === 0) {
    alert("No items to export");
    return;
  }

  // Format data for CSV
  const csvData = items.map((item) => ({
    "Item ID": item.walletItemId,
    Type: item.type,
    Value: getItemValue(item),
    Status: item.status,
    "Created At": formatDateForCSV(item.createdAt),
    "Expires At": item.expiresAt ? formatDateForCSV(item.expiresAt) : "Never",
    "Claimed At": item.claimedAt ? formatDateForCSV(item.claimedAt) : "N/A",
  }));

  // Convert to CSV
  const csv = Papa.unparse(csvData, {
    header: true,
    skipEmptyLines: true,
  });

  downloadCSV(csv, fileName);
};

/**
 * Export detailed transaction report
 */
export const exportDetailedReport = (
  transactions: LedgerTransaction[],
  walletItems: WalletItem[],
  userName: string = "User"
) => {
  if (!transactions || transactions.length === 0) {
    alert("No data to export");
    return;
  }

  // Create summary
  const totalIssued = transactions
    .filter((tx) => tx.type === "ISSUE")
    .reduce((sum, tx) => sum + (tx.amount || 0), 0);

  const totalRedeemed = transactions
    .filter((tx) => tx.type === "REDEEM")
    .reduce((sum, tx) => sum + (tx.amount || 0), 0);

  const activeItems = walletItems.filter((item) => item.status === "ACTIVE").length;

  // Summary section
  const summary = [
    ["Detailed Wallet Report", userName],
    ["Generated", new Date().toLocaleDateString("no-NO")],
    [""],
    ["SUMMARY"],
    ["Total Issued (NOK)", totalIssued],
    ["Total Redeemed (NOK)", totalRedeemed],
    ["Balance (NOK)", totalIssued - totalRedeemed],
    ["Active Items", activeItems],
    [""],
    ["TRANSACTIONS"],
  ];

  // Transaction data
  const transactionData = transactions.map((tx) => [
    tx.txId,
    tx.type,
    tx.amount || "N/A",
    tx.reason || "N/A",
    tx.status,
    formatDateForCSV(tx.createdAt),
  ]);

  // Combine
  const allData = [
    ...summary,
    ["ID", "Type", "Amount", "Reason", "Status", "Date"],
    ...transactionData,
  ];

  // Convert to CSV
  const csv = allData.map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");

  downloadCSV(csv, `wallet-report-${userName}-${new Date().toISOString().split("T")[0]}.csv`);
};

/**
 * Helper: Download CSV file
 */
const downloadCSV = (csv: string, fileName: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
};

/**
 * Helper: Format item value for display
 */
const getItemValue = (item: WalletItem): string => {
  if (item.type === "GIFTCARD") {
    const used = item.usedAmount || 0;
    const remaining = (item.value || 0) - used;
    return `${remaining} / ${item.value} NOK`;
  } else if (item.type === "RANGE_TOKEN") {
    return `${item.count || 0} tokens`;
  } else if (item.type === "GREENFEE_TICKET") {
    return `${item.count || 0} x ${item.greenFeeType || "18H"}`;
  }
  return "N/A";
};

/**
 * Helper: Format date for CSV
 */
const formatDateForCSV = (date: Date | string | undefined): string => {
  if (!date) return "N/A";

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString("no-NO");
  } catch {
    return "N/A";
  }
};
